import React, { useState, useRef } from 'react';
import { Upload, Paperclip, X, FileText, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Drag and Drop File Upload Component
 * Supports both drag-and-drop and click-to-upload
 */
export function DragDropUpload({
  onUpload,
  value,
  fileName,
  fileType,
  onRemove,
  folder = 'uploads',
  accept = '.pdf,.png,.jpg,.jpeg,.webp',
  placeholder = 'Drag and drop or click to upload',
  darkMode = false,
  disabled = false,
  className = ''
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file) => {
    // Validate file type
    const allowedTypes = accept.split(',').map(t => t.trim().toLowerCase());
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    const mimeType = file.type.toLowerCase();
    
    const isValidType = allowedTypes.some(t => 
      fileExt === t || mimeType.includes(t.replace('.', ''))
    );
    
    if (!isValidType) {
      toast.error(`Invalid file type. Allowed: ${accept}`);
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = response.data.url || response.data.file_url;
      const detectedType = file.type.includes('pdf') ? 'pdf' : 'image';
      
      onUpload(fileUrl, file.name, detectedType);
      toast.success(`File "${file.name}" uploaded`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const getFileIcon = () => {
    if (fileType === 'pdf') return <FileText className="w-4 h-4 text-red-500" />;
    if (fileType === 'image') return <Image className="w-4 h-4 text-blue-500" />;
    return <Paperclip className="w-4 h-4 text-green-600" />;
  };

  // If file is already uploaded, show it
  if (value) {
    return (
      <div className={`flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-green-50 border border-green-200'} ${className}`}>
        {getFileIcon()}
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-green-700 hover:underline flex-1 truncate"
        >
          {fileName || 'View file'}
        </a>
        <Badge variant="outline" className="text-xs">
          {fileType === 'pdf' ? 'PDF' : fileType === 'image' ? 'Image' : 'File'}
        </Badge>
        {onRemove && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0"
            onClick={onRemove}
            disabled={disabled}
          >
            <X className="w-4 h-4 text-red-500" />
          </Button>
        )}
      </div>
    );
  }

  // Upload area
  return (
    <div
      className={`
        relative flex items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed 
        transition-all cursor-pointer
        ${isDragging 
          ? 'border-[#BC5D41] bg-[#BC5D41]/10' 
          : darkMode 
            ? 'border-[#2A2A2A] hover:border-[#BC5D41] hover:bg-[#1A1A1A]' 
            : 'border-gray-200 hover:border-[#BC5D41] hover:bg-gray-50'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled || isUploading}
      />
      
      {isUploading ? (
        <>
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#BC5D41]" />
          <span className="text-sm text-muted-foreground">Uploading...</span>
        </>
      ) : (
        <>
          <Upload className={`w-5 h-5 ${isDragging ? 'text-[#BC5D41]' : 'text-muted-foreground'}`} />
          <span className={`text-sm ${isDragging ? 'text-[#BC5D41]' : 'text-muted-foreground'}`}>
            {isDragging ? 'Drop file here' : placeholder}
          </span>
        </>
      )}
    </div>
  );
}

export default DragDropUpload;
